import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// 페이지 애니메이션 설정
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

function IdCardCameraPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [error, setError] = useState(null);
  const [usingFrontCamera, setUsingFrontCamera] = useState(false);
  const [showUploadOption, setShowUploadOption] = useState(false);
  const [isCameraSupported, setIsCameraSupported] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  // 자체 카메라 권한 안내 모달 표시 여부 (네이티브 권한 요청 전에 한 번 더 안내)
  const [showPermissionGuide, setShowPermissionGuide] = useState(true);
  const fileInputRef = useRef(null);

  // 카메라 권한 상태 확인 함수
  const checkCameraPermission = async () => {
    try {
      // 권한 API가 지원되는 경우
      if (navigator.permissions && navigator.permissions.query) {
        const permissionStatus = await navigator.permissions.query({
          name: "camera",
        });

        if (permissionStatus.state === "denied") {
          setPermissionDenied(true);
          setIsCameraSupported(false);
          setShowUploadOption(true);
          return false;
        }

        // 권한 상태 변경 감지
        permissionStatus.onchange = () => {
          if (permissionStatus.state === "granted") {
            setPermissionDenied(false);
            startCamera();
          } else if (permissionStatus.state === "denied") {
            setPermissionDenied(true);
            setIsCameraSupported(false);
            setShowUploadOption(true);
          }
        };
      }
    } catch (err) {
      // 권한 API가 지원되지 않는 경우 무시하고 계속 진행
      console.log("Permission API not supported");
    }
    return true;
  };

  // 카메라 지원 및 상태 테스트 함수
  const testCameraSupport = async () => {
    try {
      // HTTPS 체크 (localhost 제외)
      const isSecure =
        window.location.protocol === "https:" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      if (!isSecure) {
        setError(
          "카메라 접근을 위해서는 HTTPS 연결이 필요합니다. 안전한 연결로 접속해주세요.",
        );
        setIsCameraSupported(false);
        setShowUploadOption(true);
        return false;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setIsCameraSupported(false);
        setShowUploadOption(true);
        return false;
      }

      // 권한 상태 먼저 확인
      const hasPermission = await checkCameraPermission();
      if (!hasPermission) {
        return false;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      if (!stream || !stream.active) {
        setIsCameraSupported(false);
        setShowUploadOption(true);
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack || videoTrack.readyState !== "live") {
        setIsCameraSupported(false);
        setShowUploadOption(true);
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      const testVideo = document.createElement("video");
      testVideo.srcObject = stream;

      try {
        await new Promise((resolve, reject) => {
          testVideo.onloadedmetadata = resolve;
          testVideo.onerror = reject;
          setTimeout(reject, 5000);
        });

        await testVideo.play();

        stream.getTracks().forEach((track) => track.stop());
        return true;
      } catch (err) {
        stream.getTracks().forEach((track) => track.stop());
        setIsCameraSupported(false);
        setShowUploadOption(true);
        return false;
      }
    } catch (err) {
      setIsCameraSupported(false);
      setShowUploadOption(true);

      // 권한 거부 에러인 경우
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setPermissionDenied(true);
        setError(
          "카메라 접근 권한이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.",
        );
      }

      return false;
    }
  };

  // 권한 안내 모달에서 "허용"을 누른 뒤 카메라 지원 테스트 후 시작
  const initializeCamera = async () => {
    const isSupported = await testCameraSupport();

    if (isSupported) {
      startCamera();
    } else {
      setShowUploadOption(true);
    }
  };

  // 권한 안내 모달에서 카메라 켜기
  const handleAllowCamera = () => {
    setShowPermissionGuide(false);
    initializeCamera();
  };

  // 권한 안내 모달에서 취소 → 제출 페이지로 복귀
  const handleCancelPermission = () => {
    navigate("/id-card-submit");
  };

  // 컴포넌트 언마운트 시 카메라 정리
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // 카메라 시작 함수
  const startCamera = async (forceFront = false) => {
    try {
      // 이전 카메라 중지
      stopCamera();

      const facingMode = forceFront ? "user" : "environment";
      setUsingFrontCamera(forceFront);

      // 카메라 제약 조건
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // 스트림 참조 저장
      streamRef.current = stream;

      // 비디오 요소에 스트림 연결
      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        videoRef.current.onloadedmetadata = () => {
          videoRef.current
            .play()
            .then(() => {
              setCameraActive(true);
              setError(null);
            })
            .catch((err) => {
              setError("카메라 재생에 실패했습니다. 다시 시도해주세요.");
            });
        };
      }
    } catch (err) {
      // 카메라 접근 실패 시 파일 업로드 옵션 표시
      setIsCameraSupported(false);
      setShowUploadOption(true);
      setCameraActive(false);

      let errorMessage = "카메라 접근에 실패했습니다. ";

      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setPermissionDenied(true);
        errorMessage =
          "카메라 접근 권한이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.";
      } else if (err.name === "NotFoundError") {
        errorMessage += "카메라를 찾을 수 없습니다.";
      } else if (err.name === "NotReadableError") {
        errorMessage += "카메라가 다른 앱에서 사용 중일 수 있습니다.";
      } else {
        errorMessage += err.message || "알 수 없는 오류가 발생했습니다.";
      }

      setError(errorMessage);
    }
  };

  // 카메라 중지 함수
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  // 카메라 전환 함수
  const switchCamera = () => {
    startCamera(!usingFrontCamera);
  };

  // 이미지 파일 업로드 처리 함수
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      // 파일 선택이 취소된 경우 다시 카메라 실행
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("이미지 파일만 업로드 가능합니다.");
      // 잘못된 파일 형식인 경우 다시 카메라 실행
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      navigate("/id-card-crop", {
        state: {
          originalImage: e.target.result,
        },
      });
    };
    reader.readAsDataURL(file);
  };

  // 사진 촬영 함수 - 가이드 영역만 잘라내기
  const capturePhoto = () => {
    if (!isCameraSupported || showUploadOption) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    if (!videoRef.current || !canvasRef.current) return;

    try {
      // 가이드 프레임 요소 가져오기
      const guideFrame = document.querySelector(".id-card-guide-frame");
      if (!guideFrame) {
        return;
      }

      const video = videoRef.current;

      // 화면상 비디오 요소 / 가이드 프레임 위치
      const videoRect = video.getBoundingClientRect();
      const frameRect = guideFrame.getBoundingClientRect();

      // 카메라 원본(비디오) 해상도 및 화면 표시 크기
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;
      const displayWidth = videoRect.width;
      const displayHeight = videoRect.height;

      if (!videoWidth || !videoHeight || !displayWidth || !displayHeight) {
        return;
      }

      // 비디오는 object-fit: cover 로 표시된다.
      // cover 는 가로/세로 중 큰 배율로 꽉 채운 뒤 넘치는 부분을 잘라내므로,
      // 화면 좌표 → 원본 픽셀 좌표 변환 시 단일 배율과 잘려나간 오프셋을 반영해야
      // 가이드 프레임 영역이 정확히 잘린다. (기존엔 가로/세로 배율을 따로 써서
      // 상단이 잘리고 하단이 더 담기는 문제가 있었음)
      const scale = Math.max(
        displayWidth / videoWidth,
        displayHeight / videoHeight,
      );
      const renderedWidth = videoWidth * scale;
      const renderedHeight = videoHeight * scale;
      // cover 로 인해 좌/상단에서 화면 밖으로 잘려나간 픽셀 양(화면 기준)
      const hiddenLeft = (renderedWidth - displayWidth) / 2;
      const hiddenTop = (renderedHeight - displayHeight) / 2;

      // 가이드 프레임의 비디오 요소 기준 상대 위치(화면 픽셀)
      const relativeLeft = frameRect.left - videoRect.left;
      const relativeTop = frameRect.top - videoRect.top;

      // 원본 비디오 픽셀 좌표로 변환
      let srcX = (relativeLeft + hiddenLeft) / scale;
      let srcY = (relativeTop + hiddenTop) / scale;
      let srcWidth = frameRect.width / scale;
      let srcHeight = frameRect.height / scale;

      // 전면 카메라는 좌우 반전되어 보이므로 x 좌표를 뒤집는다
      if (usingFrontCamera) {
        srcX = videoWidth - srcX - srcWidth;
      }

      // 원본 범위를 벗어나지 않도록 보정
      srcX = Math.max(0, Math.min(srcX, videoWidth));
      srcY = Math.max(0, Math.min(srcY, videoHeight));
      srcWidth = Math.min(srcWidth, videoWidth - srcX);
      srcHeight = Math.min(srcHeight, videoHeight - srcY);

      // 최종 출력 캔버스 (가이드 프레임과 동일한 325 x 208 비율)
      const cropCanvas = document.createElement("canvas");
      cropCanvas.width = 325;
      cropCanvas.height = 208;
      const cropContext = cropCanvas.getContext("2d");

      // 전면 카메라는 미리보기와 방향을 맞추기 위해 좌우 반전하여 그린다
      if (usingFrontCamera) {
        cropContext.translate(cropCanvas.width, 0);
        cropContext.scale(-1, 1);
      }

      cropContext.drawImage(
        video,
        srcX,
        srcY,
        srcWidth,
        srcHeight,
        0,
        0,
        cropCanvas.width,
        cropCanvas.height,
      );

      // 이미지 데이터 URL 생성 및 저장
      const imageDataURL = cropCanvas.toDataURL("image/jpeg", 0.95);
      localStorage.setItem("temp_captured_image", imageDataURL);

      // 제출 페이지로 이동
      navigate("/id-card-submit", {
        replace: true,
        state: { fromCamera: true },
      });
    } catch (err) {
      setError(`이미지 캡처 오류: ${err.message}`);
    }
  };

  // 뒤로가기
  const handleBack = () => {
    navigate("/id-card-submit");
  };

  return (
    <motion.div
      className="camera-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* 카메라 권한 안내 모달 (네이티브 권한 요청 전 1회 안내) */}
      {showPermissionGuide && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            zIndex: 2000,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "18px",
              padding: "28px 22px 22px",
              width: "100%",
              maxWidth: "340px",
              textAlign: "center",
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                backgroundColor: "#EBEBFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 18px",
              }}
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9.2 5l-1.3 2H4.5A1.5 1.5 0 003 8.5v9A1.5 1.5 0 004.5 19h15a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0019.5 7h-3.4l-1.3-2H9.2z"
                  stroke="#7A4FFF"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="13"
                  r="3.4"
                  stroke="#7A4FFF"
                  strokeWidth="1.7"
                />
              </svg>
            </div>

            <h2
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#0A2540",
                margin: "0 0 10px",
              }}
            >
              카메라 접근 권한이 필요해요
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#6B7280",
                lineHeight: 1.55,
                margin: "0 0 18px",
              }}
            >
              신분증을 직접 촬영해 본인 확인을 진행하기 위해
              <br />
              카메라 권한이 필요합니다.
            </p>

            <div
              style={{
                backgroundColor: "#F7F8FA",
                borderRadius: "12px",
                padding: "16px",
                textAlign: "left",
                marginBottom: "22px",
              }}
            >
              {[
                "신분증은 본인 확인·제출 용도로만 사용돼요.",
                "다음 화면에서 권한 요청이 뜨면 ‘허용’을 눌러주세요.",
              ].map((text, idx, arr) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    marginBottom: idx < arr.length - 1 ? "10px" : "0",
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ flexShrink: 0, marginTop: "1px" }}
                  >
                    <circle cx="12" cy="12" r="10" fill="#EBEBFF" />
                    <path
                      d="M8 12.5L11 15.5L16 9"
                      stroke="#7A4FFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#374151",
                      lineHeight: 1.45,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {text}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={handleAllowCamera}
              style={{
                width: "100%",
                height: "52px",
                backgroundColor: "#7A4FFF",
                color: "#ffffff",
                border: "none",
                borderRadius: "12px",
                fontSize: "16px",
                fontWeight: 700,
                cursor: "pointer",
                WebkitTapHighlightColor: "rgba(0,0,0,0)",
                WebkitAppearance: "none",
              }}
            >
              카메라 켜기
            </button>
            <button
              onClick={handleCancelPermission}
              style={{
                width: "100%",
                marginTop: "8px",
                padding: "12px",
                background: "none",
                border: "none",
                color: "#9AA0A8",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                WebkitTapHighlightColor: "rgba(0,0,0,0)",
              }}
            >
              다음에 하기
            </button>
          </motion.div>
        </div>
      )}

      <div className="camera-header">
        <button className="camera-back-button" onClick={handleBack}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1>신분증 촬영</h1>
        <button className="camera-close-button" onClick={handleBack}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6L18 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="camera-container-fullscreen">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="camera-view-fullscreen"
          style={{ transform: usingFrontCamera ? "scaleX(-1)" : "none" }}
        />

        {/* 카메라 접근에 문제가 없을 때만 가이드 오버레이 표시 */}
        {(!showUploadOption || cameraActive) && (
          <div className="id-card-guide-overlay">
            <div className="id-card-guide-frame">
              <span className="frame-corner tl"></span>
              <span className="frame-corner tr"></span>
              <span className="frame-corner bl"></span>
              <span className="frame-corner br"></span>
            </div>
          </div>
        )}

        {error && showUploadOption && (
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              maxWidth: "90%",
              textAlign: "center",
              zIndex: 1000,
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* 가이드 텍스트 섹션 */}
      {(!showUploadOption || cameraActive) && (
        <div className="camera-guide-section">
          <p className="camera-guide-title">
            영역에 맞춘 후
            <br />
            <span className="accent">촬영 버튼을 눌러주세요</span>
          </p>
          <div className="camera-guide-sub">
            <p>
              <span className="info-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    stroke="#8A8F9A"
                    strokeWidth="1.6"
                  />
                  <path
                    d="M12 11v5M12 7.6h.01"
                    stroke="#8A8F9A"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              자동으로 촬영되지 않아요
            </p>
            <p>어두운 배경에서 촬영해 주세요</p>
            <p>빛이 반사되지 않도록 조정해 주세요</p>
          </div>
        </div>
      )}

      {(!showUploadOption || cameraActive) && (
        <div className="camera-controls">
          <button className="camera-control-button" onClick={switchCamera}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 8.5h11l-3-3M18 15.5H7l3 3"
                stroke="#7A4FFF"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="capture-cluster">
            <button className="capture-button" onClick={capturePhoto}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9.2 5l-1.3 2H4.5A1.5 1.5 0 003 8.5v9A1.5 1.5 0 004.5 19h15a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0019.5 7h-3.4l-1.3-2H9.2z"
                  stroke="#fff"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="13"
                  r="3.4"
                  stroke="#fff"
                  strokeWidth="1.7"
                />
              </svg>
            </button>
            <span className="capture-label">촬영하기</span>
          </div>
          <div className="camera-control-placeholder"></div>
        </div>
      )}

      {/* 캔버스는 숨겨둠 */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* 파일 업로드 입력 (숨겨짐) */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        style={{ display: "none" }}
      />

      {/* 카메라 지원되지 않을 때 안내 메시지 */}
      {showUploadOption && !cameraActive && (
        <div className="camera-alternative">
          <div className="upload-option">
            <h3>카메라 접근에 문제가 있습니다</h3>
            {permissionDenied ? (
              <>
                <p
                  style={{
                    marginBottom: "16px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  카메라 접근 권한이 필요합니다.브라우저 설정에 들어가셔서
                  권한을 허용해주세요.
                </p>
                {/* <div
                  style={{
                    backgroundColor: "#F3F4F6",
                    padding: "16px",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#374151",
                  }}
                >
                  <strong>크롬/엣지:</strong>
                  <br />
                  1. 주소창 왼쪽 자물쇠 아이콘 클릭
                  <br />
                  2. "사이트 설정" 클릭
                  <br />
                  3. 카메라 권한을 "허용"으로 변경
                  <br />
                  <br />
                  <strong>사파리:</strong>
                  <br />
                  1. Safari → 환경설정 → 웹사이트
                  <br />
                  2. 카메라에서 해당 사이트 허용
                </div> */}
              </>
            ) : (
              <p>
                신분증을 촬영하신 후, 신분증 규격에 맞게 이미지를 조정하여
                업로드해 주시기 바랍니다.
              </p>
            )}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <button
                className="upload-button"
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
                style={{
                  width: "100%",
                  padding: "16px",
                  backgroundColor: "#7B42F6",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                갤러리에서 선택
              </button>
              {!permissionDenied && (
                <button
                  onClick={() => {
                    setShowUploadOption(false);
                    setError(null);
                    setPermissionDenied(false);
                    startCamera();
                  }}
                  style={{
                    width: "100%",
                    padding: "16px",
                    backgroundColor: "transparent",
                    color: "#7B42F6",
                    border: "2px solid #7B42F6",
                    borderRadius: "12px",
                    fontSize: "16px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  카메라 다시 시도
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default IdCardCameraPage;
